import { useMemo, useEffect, useState } from "react";
import { usePlantRecord } from "../scripts/queries/usePlantRecordQueries";
import { strMonthMap, monthMap } from "../utils/MonthMaps";
const MAX_PER_MONTH = 43200;

export const useRecordNormalizer = (category, selectedMonths, area, fullMonths) => {

    const [isReady, setIsReady] = useState(false);

    const getNextMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 1);
    }

    const getRange = (months) => {
        if(!Array.isArray(months) || months.length === 0) {
            return {startDate: null, endDate: null};
        }

        const dates = months.map(name => {
            const parts = name.toLowerCase().split(' ');
            
            const [month, year] = parts;
            
            const normalizedMonth = strMonthMap[month];
            const monthIndex = monthMap[normalizedMonth];

            return new Date(year, monthIndex, 1);
        }).filter(date => !isNaN(date));

        if (dates.length === 0) {
            return {startDate: null, endDate: null};
        }

        const startDate = new Date(Math.min(...dates));
        const endDate = getNextMonth(new Date(Math.max(...dates)));

        const formatDate = (date) => date.toISOString().split('T')[0];

        return {
            startDate: formatDate(startDate),
            endDate: formatDate(endDate)
        }
    }

    const dateRange = getRange(selectedMonths);

    //The fetch will always bring the data of the full date range, after that, it'll be cached and can be filtered by months
    const params = useMemo(() => ({
        startDate: getRange(fullMonths).startDate,
        endDate: getRange(fullMonths).endDate,
        area: area
    }), [dateRange.startDate, dateRange.endDate, area]);

    useEffect(() => {
        if (!params.startDate || !params.endDate) return;

        setIsReady(false);
        const handler = setTimeout(() => {
            setIsReady(true);
        }, 500);

        return () => clearTimeout(handler);
    }, [params]);

    const {data: json, isLoading, isFetching} = usePlantRecord(params, {enabled: isReady});

    //Groups and sums the data by machine-area
    const groupedData = useMemo(() => {
        const grouped = {};

        if (!selectedMonths || selectedMonths.length === 0) {
            return [];
        }

        const dataArr = (json && typeof json === 'object' && !Array.isArray(json))  //Make sure it's an array
            ? Object.values(json)
            : (Array.isArray(json) ? json : []); 
        
        if (dataArr.length === 0) {
            return [];
        }

        const updatedArr = dataArr.map(item => ({
            ...item, area: category[item.nombre] || item.area   //Updates the groups with the user's selection
        }))

        updatedArr.forEach(item => {
            if (!item || !item.nombre || !item.area || !Array.isArray(item.valores)) {  
                return;
            }
            
            const key = `${item.nombre}-${item.area}`;  //Group elements by name-area

            if (!grouped[key]) {
                grouped[key] = {    //Creates an array with default values for each group
                    nombre: item.nombre,
                    area: item.area,
                    setup: 0,
                    valores: [0, 0, 0],  
                    totalSum: 0,
                    contributingMonths: new Set()
                };
            }

            const group = grouped[key];
            group.contributingMonths.add(item.mes);
        
            if (typeof item.setup === 'number' && !isNaN(item.setup)) {
                group.setup += item.setup;
            }

            //Add the first three values
            item.valores.slice(0, 3).forEach((val, index) => {
                if (typeof val === 'number' && !isNaN(val)) {
                    group.valores[index] += val;
                    group.totalSum += val;
                }
            })
        });
        
        return Object.values(grouped);
    }, [selectedMonths, json, category]);   //Recalculate if a month is selected

    // Calculates the excess and gives format to the final array (the one with the excess)
    const processData = useMemo(() => {
        return groupedData.map(item => {
            const months = item.contributingMonths.size;
            
            if (months === 0 || item.totalSum === 0) {
                return null;
            }

            const totalMax = months > 0 ? MAX_PER_MONTH * months : MAX_PER_MONTH;
            let excess = 0;

            //Calculate the excess
            if (item.totalSum > totalMax) {
                excess = item.totalSum - totalMax;
            }

            return {
                key: `${item.nombre}-${item.area}`,
                nombre: item.nombre,
                area: item.area,
                setup: item.setup,
                contributingMonths: item.contributingMonths,
                valores: [...item.valores, excess]
            };
        }).filter(Boolean) //Exclude the null values
    }, [groupedData]);

    const groupedByArea = useMemo(() => {
        const groups = {
            FLEXO: [], 
            OFFSET: [],
            OTROS: []
        };

        processData.forEach(item => {
            if(item.area.startsWith('FLEXO')) {
                groups.FLEXO.push(item);
            } else if (item.area.startsWith('OFFSET')) {
                groups.OFFSET.push(item);
            } else {
                groups.OTROS.push(item);
            }
        });

        return groups;
    }, [processData]);

    const lineProcessData = useMemo(() => {
        const grouped = {};

        if (!selectedMonths || selectedMonths.length < 2) {
            return {};
        }

        const dataArr = (json && typeof json === 'object' && !Array.isArray(json)) ? Object.values(json) : (Array.isArray(json) ? json : []);
        
        if (dataArr.length === 0) {
            return {};
        }

        const updatedArr = dataArr.map(item => ({
            ...item, area: category[item.nombre] || item.area
        }))

        updatedArr.forEach(item => {
            if (!item || !item.area || !item.nombre || !Array.isArray(item.valores)) {
                return;
            }

            const key = `${item.nombre}-${item.area}`

            if (!grouped[key]) {
                grouped[key] = {
                    key: key,
                    nombre: item.nombre,
                    area: item.area,
                    values: {}
                };
            }

            const group = grouped[key];
            const month = item.mes;

            if (selectedMonths.includes(month)) {
                if (!group.values[month]) {
                    group.values[month] = {
                        valores: [0, 0, 0],
                        setup: 0,
                        totalSum: 0
                    };
                }

                const monthGroup = group.values[month];
                if(typeof item.setup === 'number' && !isNaN(item.setup)) {
                    monthGroup.setup += item.setup;
                }

                item.valores.slice(0, 3).forEach((val, index) => {
                    if (typeof val === 'number' && !isNaN(val)) {
                        monthGroup.valores[index] += val;
                        monthGroup.totalSum += val;
                    }
                });
            }
        });

        //Get the excess for each month
        Object.values(grouped).forEach(group => {
            Object.keys(group.values).forEach(month => {
                const monthData = group.values[month];
                let excess = 0;

                if (monthData.totalSum > MAX_PER_MONTH) {
                    excess = monthData.totalSum - MAX_PER_MONTH;
                }

                monthData.valores.push(excess);
            });
        });

        return grouped;
    }, [json, category, selectedMonths]);

    const dataByArea = useMemo(() => {
        const groups = {FLEXO: [], OFFSET: [], OTROS: []};

        if (selectedMonths.length < 2 ) {
            return groups;
        }

        Object.values(lineProcessData).forEach(item => {
            if (item.area.startsWith('FLEXO')) {
                groups.FLEXO.push(item);
            } else if (item.area.startsWith('OFFSET')) {
                groups.OFFSET.push(item);
            } else {
                groups.OTROS.push(item);
            }
        });
        return groups;
    }, [lineProcessData, selectedMonths]);

    return {groupedByArea, dataByArea, loading: isLoading || isFetching, data: json}
}